import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Crown, Globe2, Loader2, MapPin, Trophy, Users } from "lucide-react";
import { getTeamTrackRanking, getTopRanking, getTrackRanking } from "../../api/leaderboardApi";
import { formatDuration, formatNumber } from "../Teams/teamIdentity";
import { getUserDisplayName, isBestTimeCandidate } from "../../lib/gameResults";

const ROWS_SHOWN = 10;

const RANK_STYLES = {
  1: "bg-amber-400 text-slate-900",
  2: "bg-slate-300 text-slate-900",
  3: "bg-orange-300 text-slate-900",
};

const playerResultOf = (session, userId) => {
  const results = [
    ...(Array.isArray(session?.playerResults) ? session.playerResults : []),
    ...(Array.isArray(session?.results) ? session.results : []),
  ];
  return results.find((item) => String(item.userId) === String(userId)) || null;
};

/**
 * One leaderboard per game the player has a result in: time games by track,
 * score games by game mode. Newest first.
 */
const buildBoards = (sessions, userId) => {
  const boards = new Map();

  sessions.forEach((session) => {
    const result = playerResultOf(session, userId);
    if (!result) return;

    const gameModeId = result.gameModeId ?? session.gameModeId ?? session.gameMode?.id;
    if (!gameModeId) return;

    const resultType = result.resultType === "time" ? "time" : "score";
    const trackName = resultType === "time" ? result.trackName || "" : "";
    const key = `${gameModeId}|${resultType}|${trackName}`;
    const playedAt = new Date(session.createdAt || 0).getTime();

    let board = boards.get(key);
    if (!board) {
      board = {
        key,
        gameModeId,
        resultType,
        trackName,
        title: session.gameMode?.title || `Game ${gameModeId}`,
        lastPlayedAt: 0,
        countryId: null,
        coopTeamId: null,
        coopPlayedAt: 0,
        personalBestTime: null,
        personalBestScore: null,
      };
      boards.set(key, board);
    }

    if (playedAt >= board.lastPlayedAt) {
      board.lastPlayedAt = playedAt;
      board.countryId = result.countryId ?? board.countryId;
    }

    if (resultType === "time" && isBestTimeCandidate(result)) {
      const time = Number(result.finishTime);
      board.personalBestTime = board.personalBestTime === null ? time : Math.min(board.personalBestTime, time);
    }

    const score = Number(result.score);
    if (resultType === "score" && Number.isFinite(score)) {
      board.personalBestScore = board.personalBestScore === null ? score : Math.max(board.personalBestScore, score);
    }

    // The player's most recent Coop team on this board, for the team leaderboard.
    const team = Array.isArray(session.playerTeams) ? session.playerTeams[0] : null;
    if (session.gameSessionType === "Coop" && team?.id && playedAt >= board.coopPlayedAt) {
      board.coopTeamId = team.id;
      board.coopPlayedAt = playedAt;
    }
  });

  return Array.from(boards.values()).sort((a, b) => b.lastPlayedAt - a.lastPlayedAt);
};

const RankBadge = ({ rank }) => (
  <span
    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums ${
      RANK_STYLES[rank] || "bg-gray-100 text-gray-600"
    }`}
  >
    {rank || "-"}
  </span>
);

const LeaderboardRow = ({ rank, name, detail, value, isYou, youLabel }) => (
  <li
    className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${
      isYou ? "bg-orange-50 ring-1 ring-orange-200" : "bg-gray-50"
    }`}
  >
    <RankBadge rank={rank} />
    <div className="min-w-0 flex-1">
      <p className={`truncate font-semibold ${isYou ? "text-orange-800" : "text-gray-900"}`}>
        {name}
        {isYou && <span className="ms-1 text-xs font-normal text-orange-600">({youLabel})</span>}
      </p>
      {detail && <p className="truncate text-xs text-gray-500">{detail}</p>}
    </div>
    <span className="shrink-0 font-bold tabular-nums text-gray-900">{value}</span>
  </li>
);

/**
 * Leaderboards for the games the player has played: world and country
 * rankings, and the Coop team ranking for tracks they played as a team.
 */
const LeaderboardSection = ({ sessions, userData }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const userId = userData?.id;

  const boards = useMemo(() => buildBoards(sessions, userId), [sessions, userId]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [tab, setTab] = useState("world");
  const [data, setData] = useState({ world: [], country: [], teams: [], team: null });
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const board = boards.find((item) => item.key === selectedKey) || boards[0] || null;
  const countryId = userData?.countryId ?? board?.countryId ?? null;
  const isTrackBoard = board?.resultType === "time" && Boolean(board?.trackName);

  useEffect(() => {
    if (!board) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    const load = async () => {
      if (isTrackBoard) {
        const [ranking, teamRanking] = await Promise.all([
          getTrackRanking({
            gameModeId: board.gameModeId,
            trackName: board.trackName,
            countryId,
            excludeLost: true,
          }),
          board.coopTeamId
            ? getTeamTrackRanking({
                gameModeId: board.gameModeId,
                trackName: board.trackName,
                teamId: board.coopTeamId,
              }).catch(() => null)
            : Promise.resolve(null),
        ]);
        return {
          world: ranking?.worldRankings || [],
          country: ranking?.countryRankings || [],
          teams: teamRanking?.teams || [],
          team: teamRanking?.team || null,
        };
      }

      const top = await getTopRanking({ gameModeId: board.gameModeId, resultType: board.resultType });
      return { world: Array.isArray(top) ? top : [], country: [], teams: [], team: null };
    };

    load()
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [board?.key, board?.gameModeId, board?.trackName, board?.resultType, board?.coopTeamId, countryId, isTrackBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!boards.length) return null;

  const tabs = [
    { key: "world", label: t("leaderboardWorld", { defaultValue: "World" }), icon: Globe2 },
    ...(isTrackBoard && countryId
      ? [{ key: "country", label: t("leaderboardCountry", { defaultValue: "Country" }), icon: MapPin }]
      : []),
    ...(isTrackBoard && board.coopTeamId
      ? [{ key: "teams", label: t("leaderboardTeams", { defaultValue: "Teams" }), icon: Users }]
      : []),
  ];
  const activeTab = tabs.some((item) => item.key === tab) ? tab : "world";
  const youLabel = t("you", { defaultValue: "you" });

  const formatValue = (entry) =>
    board.resultType === "time"
      ? formatDuration(entry.bestTime)
      : formatNumber(entry.maxScore ?? entry.bestScore ?? entry.score, locale);

  const playerRows = (activeTab === "country" ? data.country : data.world).slice(0, ROWS_SHOWN);
  const youOnPlayerBoard = playerRows.some((entry) => String(entry.userId) === String(userId));
  const personalBest =
    board.resultType === "time"
      ? board.personalBestTime !== null
        ? formatDuration(board.personalBestTime)
        : null
      : board.personalBestScore !== null
        ? formatNumber(board.personalBestScore, locale)
        : null;

  const teamRows = data.teams.slice(0, ROWS_SHOWN);
  const yourTeamOnBoard = teamRows.some((entry) => String(entry.teamId) === String(board.coopTeamId));

  return (
    <section className="mt-8 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200 md:p-8">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Trophy size={22} className="text-orange-500" aria-hidden="true" />
            {t("leaderboards", { defaultValue: "Leaderboards" })}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("leaderboardsSubtitle", { defaultValue: "See where you stand in the games you've played." })}
          </p>
        </div>

        {boards.length > 1 && (
          <label className="block">
            <span className="sr-only">{t("leaderboardGame", { defaultValue: "Game" })}</span>
            <select
              value={board.key}
              onChange={(event) => {
                setSelectedKey(event.target.value);
                setTab("world");
              }}
              className="min-h-[44px] w-full rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100 md:w-auto"
            >
              {boards.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.title}
                  {item.trackName ? ` • ${item.trackName}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => setTab(key)}
            className={`inline-flex min-h-[40px] items-center gap-2 rounded-full px-4 text-sm font-semibold transition ${
              activeTab === key ? "bg-[#120239] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <Icon size={16} aria-hidden="true" />
            {label}
          </button>
        ))}
        {boards.length === 1 && (
          <span className="ms-auto rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
            {board.title}
            {board.trackName ? ` • ${board.trackName}` : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin" aria-hidden="true" />
          {t("loadingLeaderboard", { defaultValue: "Loading leaderboard..." })}
        </div>
      ) : loadError ? (
        <div role="alert" className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-gray-500">
          {loadError.message || t("leaderboardLoadFailed", { defaultValue: "Could not load the leaderboard." })}
        </div>
      ) : activeTab === "teams" ? (
        <>
          {teamRows.length === 0 ? (
            <p className="rounded-3xl bg-gray-50 p-8 text-center text-sm text-gray-500">
              {t("teamLeaderboardEmpty", { defaultValue: "No team has finished this track yet." })}
            </p>
          ) : (
            <ol className="space-y-2">
              {teamRows.map((entry) => (
                <LeaderboardRow
                  key={entry.teamId}
                  rank={entry.rank}
                  name={entry.teamName}
                  detail={(entry.members || []).map(getUserDisplayName).join(" & ")}
                  value={formatDuration(entry.bestTime)}
                  isYou={String(entry.teamId) === String(board.coopTeamId)}
                  youLabel={t("yourTeam", { defaultValue: "your team" })}
                />
              ))}
            </ol>
          )}
          {!yourTeamOnBoard && data.team?.rank && (
            <p className="mt-3 flex items-center gap-2 rounded-2xl bg-orange-50 px-4 py-3 text-sm text-orange-800">
              <Crown size={16} aria-hidden="true" />
              {t("yourTeamStanding", {
                defaultValue: "Your team is #{{rank}} with {{time}}",
                rank: data.team.rank,
                time: formatDuration(data.team.bestTime),
              })}
            </p>
          )}
        </>
      ) : (
        <>
          {playerRows.length === 0 ? (
            <p className="rounded-3xl bg-gray-50 p-8 text-center text-sm text-gray-500">
              {t("leaderboardEmpty", { defaultValue: "No results on this leaderboard yet." })}
            </p>
          ) : (
            <ol className="space-y-2">
              {playerRows.map((entry, index) => (
                <LeaderboardRow
                  key={`${entry.userId}-${index}`}
                  rank={index + 1}
                  name={getUserDisplayName(entry.users)}
                  detail={entry.countries?.name}
                  value={formatValue(entry)}
                  isYou={String(entry.userId) === String(userId)}
                  youLabel={youLabel}
                />
              ))}
            </ol>
          )}
          {!youOnPlayerBoard && personalBest && (
            <p className="mt-3 flex items-center gap-2 rounded-2xl bg-orange-50 px-4 py-3 text-sm text-orange-800">
              <Crown size={16} aria-hidden="true" />
              {t("notOnBoardYet", {
                defaultValue: "Your best is {{value}}. Beat the board to get on it!",
                value: personalBest,
              })}
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default LeaderboardSection;
